import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import type { TableCellProps } from "@mui/material/TableCell";
import { Sparkles } from "lucide-mui";
import type { ReactNode } from "react";
import type { Prospect } from "../../api/client.ts";
import { useTenant } from "../../contexts/TenantContext.tsx";
import type { SortDir } from "../../hooks/useUrlState.ts";
import { PROSPECT_REVIEW_COPY, useProspectReview } from "../../pages/prospects/prospectReview.ts";
import { formatNumber } from "../../utils/format.ts";
import AgentActivity from "../AgentActivity.tsx";
import CountryName from "../CountryName.tsx";
import CrmPresenceChip from "../CrmPresenceChip.tsx";
import IdentityCell from "../IdentityCell.tsx";
import RelativeTime from "../RelativeTime.tsx";
import type { RowAction } from "../RowActions.tsx";
import { TenantAvatar } from "../TenantAvatar.tsx";
import ReviewRowActions from "../review/ReviewRowActions.tsx";
import ReviewStatusChips from "../review/ReviewStatusChips.tsx";
import { isBackFromSnooze } from "../review/snooze.ts";
import EmptyTableRow from "../table/EmptyTableRow.tsx";
import ScrollTable from "../table/ScrollTable.tsx";
import SortableTableHead, { type SortableColumn } from "../table/SortableTableHead.tsx";
import { hitKindIcon } from "./hitKinds.tsx";
import ProspectSourcesCell from "./ProspectSourcesCell.tsx";
import type { ProspectSelection } from "./useProspectListView.ts";

/** The shared columns a page may hide (the company and the actions always show). */
export type ProspectBaseColumn = "found_by" | "hits" | "status" | "time";

/** A page's own column, drawn after Status (a collection's contacts, labels, …). */
export interface ProspectExtraColumn {
  column: SortableColumn & { key: string };
  render: (p: Prospect) => ReactNode;
  align?: TableCellProps["align"];
  /** The cell handles its own clicks (links, chips): they don't open the row. */
  interactive?: boolean;
}

export interface ProspectTableProps {
  rows: Prospect[] | undefined;
  /** Rows gone from the server, held in place by `useLiveList`: dimmed. */
  removedIds?: ReadonlySet<string>;
  /** "done": the time column is when it was finished, not the last hit. */
  timeColumn?: "last_hit" | "done";
  extraColumns?: ProspectExtraColumn[];
  hiddenColumns?: readonly ProspectBaseColumn[];
  /** More after the status chips (a collection's own state for the row). */
  statusExtra?: (p: Prospect) => ReactNode;
  sortBy: string;
  sortDir: SortDir;
  onSort: (column: string) => void;
  /** The open row, and the one highlighted (the open one, else the last opened). */
  openId: string | null;
  highlightedId: string | null;
  onOpen: (p: Prospect) => void;
  /** The open row's ref, to bring it into view. */
  scrollToRow?: (el: HTMLTableRowElement | null) => void;
  selection: ProspectSelection;
  canDecide: boolean;
  canContinue: boolean;
  /** More kebab actions per row (a collection's "Remove from collection"). */
  rowActions?: (p: Prospect) => RowAction[];
  emptyCopy: ReactNode;
  /** More bodies after the rows (the worklist's snoozed section). */
  after?: ReactNode;
}

/** The columns a `ProspectTable` draws, for a section row's colSpan. */
export function prospectColSpan({
  extraColumns = [],
  hiddenColumns = [],
  canDecide,
}: Pick<ProspectTableProps, "extraColumns" | "hiddenColumns" | "canDecide">): number {
  // Company, the shared four less the hidden ones, the page's own, and the
  // checkbox and actions cells.
  return 1 + (4 - hiddenColumns.length) + extraColumns.length + (canDecide ? 2 : 0);
}

/**
 * The tenant's prospects as rows: the company, what found it, its hits, where
 * it stands, when last hit, and the review actions; a page adds its own
 * columns. Clicking a row opens it (`onOpen`, the drawer).
 */
export default function ProspectTable(props: ProspectTableProps) {
  const {
    rows,
    removedIds,
    timeColumn = "last_hit",
    extraColumns = [],
    hiddenColumns = [],
    sortBy,
    sortDir,
    onSort,
    canDecide,
    selection,
    emptyCopy,
    after,
  } = props;
  const shown = (key: ProspectBaseColumn, column: SortableColumn) =>
    hiddenColumns.includes(key) ? [] : [column];
  const columns: SortableColumn[] = [
    { id: "company", label: "Company" },
    ...shown("found_by", { id: null, label: "Found by", width: 260 }),
    ...shown("hits", { id: "hits", label: "Hits", align: "right", width: 90 }),
    ...shown("status", { id: null, label: "Status", width: 220 }),
    ...extraColumns.map((c) => c.column),
    ...shown("time", {
      id: timeColumn,
      label: timeColumn === "done" ? "Done" : "Last hit",
      width: 140,
    }),
    ...(canDecide ? [{ id: null, label: "", key: "actions", width: 160 } satisfies SortableColumn] : []),
  ];
  const colSpan = prospectColSpan(props);
  const pageIds = rows?.map((p) => p.id) ?? [];

  return (
    <ScrollTable>
      <SortableTableHead
        columns={columns}
        sortBy={sortBy}
        sortDir={sortDir}
        onSort={onSort}
        leading={
          canDecide ? (
            <TableCell padding="checkbox">
              <Checkbox
                size="small"
                checked={selection.allSelected}
                indeterminate={selection.someSelected && !selection.allSelected}
                onChange={selection.toggleAll}
                disabled={pageIds.length === 0}
                slotProps={{ input: { "aria-label": "Select all on this page" } }}
              />
            </TableCell>
          ) : undefined
        }
      />
      <TableBody>
        {rows?.length === 0 && (
          <EmptyTableRow colSpan={colSpan} align="left">
            {emptyCopy}
          </EmptyTableRow>
        )}
        {rows?.map((p) => <ProspectRow key={p.id} prospect={p} removed={removedIds?.has(p.id)} {...props} />)}
      </TableBody>
      {after}
    </ScrollTable>
  );
}

/** One prospect's row; `muted` greys it out (snoozed), `removed` dims it (gone from the list). */
export function ProspectRow({
  prospect: p,
  muted = false,
  removed = false,
  timeColumn = "last_hit",
  extraColumns = [],
  hiddenColumns = [],
  statusExtra,
  openId,
  highlightedId,
  onOpen,
  scrollToRow,
  selection,
  canDecide,
  canContinue,
  rowActions,
}: Omit<ProspectTableProps, "rows" | "removedIds" | "sortBy" | "sortDir" | "onSort" | "emptyCopy" | "after"> & {
  prospect: Prospect;
  muted?: boolean;
  removed?: boolean;
}) {
  const { tenantPath } = useTenant();
  const hidden = (key: ProspectBaseColumn) => hiddenColumns.includes(key);
  return (
    <TableRow
      ref={p.id === openId ? scrollToRow : undefined}
      hover
      selected={p.id === highlightedId || selection.selected.has(p.id)}
      onClick={() => onOpen(p)}
      aria-disabled={removed || undefined}
      sx={{
        cursor: "pointer",
        ...(muted && { opacity: 0.55, "&:hover": { opacity: 1 } }),
        ...(removed && { opacity: 0.5 }),
        // A row waiting on a person carries a warning bar, one back from a
        // snooze or reopened an info bar; the open row's wins.
        ...(p.awaiting_approval
          ? { "& > td:first-of-type": { boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.warning.main}` } }
          : (isBackFromSnooze(p.snoozed_until) || p.reopened_at) && {
              "& > td:first-of-type": { boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.info.main}` },
            }),
        ...(p.id === highlightedId && {
          "&.Mui-selected > td:first-of-type": {
            boxShadow: (theme) => `inset 3px 0 0 ${theme.palette.primary.main}`,
          },
        }),
      }}
    >
      {canDecide && (
        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
          <Checkbox
            size="small"
            checked={selection.selected.has(p.id)}
            onChange={() => selection.toggle(p.id)}
            slotProps={{ input: { "aria-label": "Select prospect" } }}
          />
        </TableCell>
      )}
      <TableCell sx={{ maxWidth: 320 }}>
        <IdentityCell
          avatar={<TenantAvatar name={p.company_name ?? "?"} size={32} />}
          name={p.company_name ?? "Unnamed company"}
          to={p.master_company_id ? tenantPath(`/companies/${p.master_company_id}`) : undefined}
          tag={
            (!p.resolved || p.active_agent_runs.length > 0) && (
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
                <AgentActivity runs={p.active_agent_runs} />
                {!p.resolved && <Chip size="small" variant="outlined" color="warning" label="Not in database" />}
              </Box>
            )
          }
          caption={
            <>
              {p.company_country && <CountryName code={p.company_country} showName={false} variant="inherit" />}
              {p.company_country && p.company_domain && " · "}
              {p.company_domain}
            </>
          }
        />
      </TableCell>
      {!hidden("found_by") && (
        <TableCell>
          <ProspectSourcesCell sources={p.sources} dense />
        </TableCell>
      )}
      {!hidden("hits") && (
        <TableCell align="right">
          <Stack direction="row" spacing={0.5} sx={{ justifyContent: "flex-end", alignItems: "center" }}>
            <Typography variant="body2" sx={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>
              {formatNumber(p.hit_count)}
            </Typography>
            {p.hits_since_decision > 0 && p.review_state !== "pending" && (
              <Tooltip title="Prospected again since the last decision on it">
                <Chip size="small" color="warning" variant="outlined" icon={<Sparkles />} label={`${p.hits_since_decision} new`} />
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      )}
      {!hidden("status") && (
        <TableCell>
          {statusExtra || p.crm ? (
            <Stack direction="row" spacing={0.5} useFlexGap sx={{ alignItems: "center", flexWrap: "wrap" }}>
              <ReviewStatusChips subject={p} reopenedAt={p.reopened_at} />
              {statusExtra?.(p)}
              <CrmPresenceChip crm={p.crm} />
            </Stack>
          ) : (
            <ReviewStatusChips subject={p} reopenedAt={p.reopened_at} />
          )}
        </TableCell>
      )}
      {extraColumns.map(({ column, render, align, interactive }) => (
        <TableCell
          key={column.key}
          align={align ?? column.align}
          onClick={interactive ? (e) => e.stopPropagation() : undefined}
        >
          {render(p)}
        </TableCell>
      ))}
      {!hidden("time") && (
        <TableCell>
          <Stack direction="row" spacing={0.75} sx={{ alignItems: "center", color: "text.secondary" }}>
            {timeColumn !== "done" && hitKindIcon(p.last_hit_kind, { fontSize: "inherit" })}
            <Typography variant="body2" component="span">
              <RelativeTime value={timeColumn === "done" ? p.done_at : p.last_hit_at} />
            </Typography>
          </Stack>
        </TableCell>
      )}
      {canDecide && (
        <TableCell padding="checkbox" align="right">
          <ReviewRowActions
            subject={p}
            name={p.company_name ?? "prospect"}
            canDecide={canDecide}
            canContinue={canContinue}
            useReview={useProspectReview}
            copy={PROSPECT_REVIEW_COPY}
            extraActions={rowActions?.(p)}
          />
        </TableCell>
      )}
    </TableRow>
  );
}
